import { useState, useEffect, useMemo, useRef } from "react";
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, FileText, Calendar, ListChecks, Coins, ArrowRight, Lock, Mail, BookOpen, ShieldAlert, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WizardShell, OptionCard, PrimaryBtn, BackBtn, IdentityFields } from "@/components/mockups/WizardUI";
import SuccessModal from "@/components/mockups/SuccessModal";
import { submitInbox, extractError, PUBLIC_API_BASE, fetchServiceStatus } from "@/lib/publicApi";
import SuspendedNotice from "@/components/SuspendedNotice";
import Honeypot, { honeypotValue } from "@/components/Honeypot";
import { validateIdentity, focusFirstError, buildErrorSummary } from "@/lib/formValidation";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = PUBLIC_API_BASE;

// ---------- helpers ----------
function newSessionId() {
  return `diag-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Verifica visibilidade condicional de um passo dado o estado das respostas
function isStepVisible(step, answers) {
  if (!step.visible_when) return true;
  const cond = step.visible_when;
  const val = answers[cond.step_key];
  if (val === undefined || val === null) return false;
  if (Array.isArray(val)) {
    return (cond.includes || []).some((v) => val.includes(v));
  }
  return (cond.includes || []).includes(val);
}

// Divide markdown por secções ## Titulo — devolve [{title, body}]
function parseSections(md) {
  if (!md) return [];
  const lines = md.split(/\r?\n/);
  const sections = [];
  let current = null;
  for (const line of lines) {
    const m = line.match(/^##\s+(.+)$/);
    if (m) {
      if (current) sections.push(current);
      current = { title: m[1].trim(), body: "" };
    } else if (current) {
      current.body += line + "\n";
    } else if (line.trim()) {
      // pré-header: acumular em preamble
      if (!sections.length) {
        current = { title: "", body: line + "\n" };
      }
    }
  }
  if (current) sections.push(current);
  return sections;
}

const SECTION_ICON = {
  "Resumo": FileText,
  "Resumo Executivo": FileText,
  "Enquadramento": BookOpen,
  "Obrigações Legais Aplicáveis": AlertTriangle,
  "Periodicidade & Prazos": Calendar,
  "Riscos de Não Conformidade": ShieldAlert,
  "Benefícios da Regularização": ShieldCheck,
  "Ações Recomendadas": ListChecks,
  "Estimativa Indicativa": Coins,
  "Próximo Passo": ArrowRight,
};

// Renderiza markdown inline: ***bold+italic***, **bold**, *italic* / _italic_
function renderInline(text) {
  // Regex captura, por ordem: ***txt***, **txt**, *txt* (não vazio), _txt_
  const re = /(\*\*\*[^*]+?\*\*\*|\*\*[^*]+?\*\*|\*(?!\s)[^*\n]+?\*|_[^_\n]+?_)/g;
  const parts = text.split(re).filter((p) => p !== undefined && p !== "");
  return parts.map((p, i) => {
    if (p.startsWith("***") && p.endsWith("***")) {
      return (
        <strong key={i} className="font-semibold italic text-slate-800 dark:text-slate-100">
          {p.slice(3, -3)}
        </strong>
      );
    }
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-slate-800 dark:text-slate-100">
          {p.slice(2, -2)}
        </strong>
      );
    }
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) {
      return (
        <em key={i} className="italic text-slate-700 dark:text-slate-200">
          {p.slice(1, -1)}
        </em>
      );
    }
    if (p.startsWith("_") && p.endsWith("_") && p.length > 2) {
      return (
        <em key={i} className="italic text-slate-700 dark:text-slate-200">
          {p.slice(1, -1)}
        </em>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

// Corpo de secção: reconhece sub-secções ### e alterna entre listas e parágrafos
function SectionBody({ title, body }) {
  const rawLines = body.split(/\r?\n/);
  // Agrupar em blocos: subheader, bullet-list, num-list, paragraph
  const blocks = [];
  let cur = null;
  const flush = () => { if (cur) { blocks.push(cur); cur = null; } };
  for (const raw of rawLines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { flush(); continue; }
    const sub = line.match(/^###\s+(.+)$/);
    if (sub) { flush(); blocks.push({ kind: "sub", text: sub[1].trim() }); continue; }
    const isBullet = /^\s*[-*]\s+/.test(line);
    const isNum = /^\s*\d+\.\s+/.test(line);
    if (isBullet) {
      if (!cur || cur.kind !== "ul") { flush(); cur = { kind: "ul", items: [] }; }
      cur.items.push(line.replace(/^\s*[-*]\s+/, ""));
    } else if (isNum) {
      if (!cur || cur.kind !== "ol") { flush(); cur = { kind: "ol", items: [] }; }
      cur.items.push(line.replace(/^\s*\d+\.\s+/, ""));
    } else {
      if (!cur || cur.kind !== "p") { flush(); cur = { kind: "p", text: "" }; }
      cur.text += (cur.text ? " " : "") + line.trim();
    }
  }
  flush();

  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        if (b.kind === "sub") {
          return (
            <h5 key={i} className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 mt-2 first:mt-0">
              {renderInline(b.text)}
            </h5>
          );
        }
        if (b.kind === "p") {
          return (
            <p key={i} className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              {renderInline(b.text)}
            </p>
          );
        }
        if (b.kind === "ul" || b.kind === "ol") {
          const Tag = b.kind === "ol" ? "ol" : "ul";
          return (
            <Tag key={i} className={`space-y-1.5 ${b.kind === "ol" ? "list-none" : ""}`}>
              {b.items.map((it, j) => (
                <li key={j} className="flex items-start gap-2 text-sm leading-relaxed">
                  {b.kind === "ol" ? (
                    <span className="mt-0.5 shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1698AC]/10 text-[#1698AC] text-[11px] font-semibold">
                      {j + 1}
                    </span>
                  ) : (
                    <CheckCircle2 size={14} className="text-[#1698AC] mt-1 shrink-0" />
                  )}
                  <span className="text-slate-700 dark:text-slate-200 flex-1">{renderInline(it)}</span>
                </li>
              ))}
            </Tag>
          );
        }
        return null;
      })}
    </div>
  );
}

// ---------- main dialog ----------
export default function DiagnosticoDialog({ open, onClose }) {
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [template, setTemplate] = useState(null); // { intro_message, steps: [...] }
  const [error, setError] = useState("");

  const [stepIdx, setStepIdx] = useState(0);       // index sobre visibleSteps
  const [answers, setAnswers] = useState({});      // {step_key: value}
  const [phase, setPhase] = useState("form");      // form | analyzing | result | identity | done
  const [aiText, setAiText] = useState("");
  const [aiError, setAiError] = useState("");
  const [id, setId] = useState({});
  const [idTouched, setIdTouched] = useState({});
  const [sending, setSending] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const abortRef = useRef(null);
  const sessionIdRef = useRef(null);
  const hpRef = useRef(null); // anti-spam (honeypot)
  const hpValRef = useRef("");

  // ─── suspensão do serviço ─────────────────────────────────────────────────
  const [suspended, setSuspended] = useState(null);
  useEffect(() => {
    if (!open) {
      setSuspended(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const status = await fetchServiceStatus();
      if (cancelled) return;
      const svc = status?.diagnostico;
      if (svc?.suspended) setSuspended({ title: svc.title, message: svc.message });
      else setSuspended(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const identityErrors = useMemo(() => {
    return validateIdentity(id, { phoneRequired: true, postalRequired: false }).errors;
  }, [id]);
  const setIdBlur = (field) => setIdTouched((t) => ({ ...t, [field]: true }));

  // fetch template on open
  useEffect(() => {
    if (!open) return;
    setLoadingTpl(true);
    setError("");
    setStepIdx(0);
    setAnswers({});
    setPhase("form");
    setAiText("");
    setAiError("");
    setId({});
    setIdTouched({});
    sessionIdRef.current = newSessionId();
    axios
      .get(`${API}/diagnostic/template`)
      .then((r) => setTemplate(r.data))
      .catch((err) => setError(extractError(err, "Não foi possível carregar o questionário.")))
      .finally(() => setLoadingTpl(false));
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [open]);

  const allSteps = template?.steps || [];
  const visibleSteps = useMemo(
    () => allSteps.filter((s) => isStepVisible(s, answers)),
    [allSteps, answers],
  );
  const totalSteps = visibleSteps.length;
  const currentStep = visibleSteps[stepIdx];

  const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const canAdvance = () => {
    if (!currentStep) return false;
    if (!currentStep.required) return true;
    const v = answers[currentStep.key];
    if (v === undefined || v === null || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  };

  const goBack = () => {
    if (phase === "identity") {
      setPhase("result");
      return;
    }
    if (phase === "result" || phase === "analyzing") {
      if (abortRef.current) abortRef.current.abort();
      setPhase("form");
      setStepIdx(Math.max(0, visibleSteps.length - 1));
      return;
    }
    setStepIdx((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    if (!canAdvance()) return;
    if (stepIdx < totalSteps - 1) {
      setStepIdx((i) => i + 1);
    } else {
      startAnalyze();
    }
  };

  // ---------- SSE streaming ----------
  const startAnalyze = async () => {
    setPhase("analyzing");
    setAiText("");
    setAiError("");
    abortRef.current = new AbortController();
    try {
      const res = await fetch(`${API}/diagnostic/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, answers }),
        signal: abortRef.current.signal,
      });
      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 120)}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let doneSse = false;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Extrair eventos completos (separados por "\n\n") sem apagar espaços dentro dos data:
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const event = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = event.split("\n");
          for (const line of lines) {
            if (!line.startsWith("data:")) continue;
            // remover apenas UM espaço delimitador SSE após "data:", preservando restantes
            let content = line.slice(5);
            if (content.startsWith(" ")) content = content.slice(1);
            if (content === "[DONE]") {
              doneSse = true;
              continue;
            }
            const decoded = content.replace(/<\|nl\|>/g, "\n");
            acc += decoded;
          }
        }
        setAiText(acc);
        if (doneSse) break;
      }
      setPhase("result");
    } catch (err) {
      if (err?.name === "AbortError") return;
      setAiError(err?.message || "Erro ao gerar diagnóstico");
      setPhase("result");
    }
  };

  // ---------- finish (identity + submit lead) ----------
  const finish = async (e) => {
    e.preventDefault();
    if (sending) return;
    // Validar identidade antes de enviar
    const { isValid, errors, firstErrorField } = validateIdentity(id, {
      phoneRequired: true,
      postalRequired: false,
    });
    if (!isValid) {
      setIdTouched({ nome: true, email: true, telefone: true });
      toast.error(buildErrorSummary(errors));
      focusFirstError(firstErrorField, "diag");
      return;
    }
    setSending(true);
    try {
      // reconstruir extra com labels legíveis
      const answersLabelled = {};
      const stepsMap = allSteps.reduce((acc, s) => ({ ...acc, [s.key]: s }), {});
      Object.entries(answers).forEach(([k, v]) => {
        const step = stepsMap[k];
        const opts = step?.options || [];
        const toLabel = (val) => opts.find((o) => o.value === val)?.label || val;
        if (Array.isArray(v)) answersLabelled[k] = v.map(toLabel);
        else if (typeof v === "boolean") answersLabelled[k] = v ? "Sim" : "Não";
        else answersLabelled[k] = toLabel(v);
      });
      const setor = answersLabelled["setor"] || "";
      const areas = Array.isArray(answersLabelled["areas"]) ? answersLabelled["areas"].join(", ") : "";
      await submitInbox({
        type: "diagnostico",
        name: id.nome,
        email: id.email,
        phone: id.telefone || "",
        subject: `Diagnóstico Legal · ${setor}${areas ? " · " + areas : ""}`,
        message: `Diagnóstico gerado via wizard IA. Sessão: ${sessionIdRef.current}. Resumo: ${aiText.slice(0, 240)}…`,
        extra: {
          session_id: sessionIdRef.current,
          answers,
          answers_labelled: answersLabelled,
          diagnostico_ai: aiText,
          website: honeypotValue(hpRef, hpValRef),
        },
      });
      // Mostrar modal de sucesso INXPEK PLUS desbloquear o relatório completo dentro do wizard.
      // O utilizador vê o diagnóstico completo aqui e recebe cópia por email.
      const details = [
        { icon: FileText, label: "Diagnóstico", value: "Legal Inteligente" },
      ];
      if (setor) details.push({ icon: Sparkles, label: "Setor", value: setor });
      if (areas) details.push({ icon: ListChecks, label: "Áreas identificadas", value: areas });
      setSuccessData({
        name: id.nome,
        email: id.email,
        details,
      });
      setPhase("unlocked");
      setSending(false);
    } catch (err) {
      toast.error(extractError(err, "Não foi possível registar. Tente novamente."));
      setSending(false);
    }
  };

  // -------------------------------
  // Render helpers per step type
  // -------------------------------
  const renderStepBody = () => {
    if (!currentStep) return null;
    const val = answers[currentStep.key];
    switch (currentStep.type) {
      case "single":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(currentStep.options || []).map((o) => (
              <OptionCard
                key={o.value}
                testId={`diag-${currentStep.key}-${o.value}`}
                selected={val === o.value}
                onClick={() => setAnswer(currentStep.key, o.value)}
                label={o.label}
              />
            ))}
          </div>
        );
      case "multi":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(currentStep.options || []).map((o) => {
              const arr = Array.isArray(val) ? val : [];
              const selected = arr.includes(o.value);
              const toggle = () => {
                const next = selected ? arr.filter((x) => x !== o.value) : [...arr, o.value];
                setAnswer(currentStep.key, next);
              };
              return (
                <OptionCard
                  key={o.value}
                  testId={`diag-${currentStep.key}-${o.value}`}
                  selected={selected}
                  onClick={toggle}
                  label={o.label}
                />
              );
            })}
          </div>
        );
      case "yesno":
        return (
          <div className="grid grid-cols-2 gap-3">
            <OptionCard
              testId={`diag-${currentStep.key}-yes`}
              selected={val === true}
              onClick={() => setAnswer(currentStep.key, true)}
              label="Sim"
            />
            <OptionCard
              testId={`diag-${currentStep.key}-no`}
              selected={val === false}
              onClick={() => setAnswer(currentStep.key, false)}
              label="Não"
            />
          </div>
        );
      case "text":
        return (
          <Input
            data-testid={`diag-${currentStep.key}-input`}
            value={val || ""}
            onChange={(e) => setAnswer(currentStep.key, e.target.value)}
            placeholder="Escreva aqui…"
          />
        );
      case "number":
        return (
          <Input
            data-testid={`diag-${currentStep.key}-input`}
            type="number"
            value={val ?? ""}
            onChange={(e) => setAnswer(currentStep.key, e.target.value ? Number(e.target.value) : "")}
            placeholder="0"
          />
        );
      case "postal":
        return (
          <Input
            data-testid={`diag-${currentStep.key}-input`}
            value={val || ""}
            onChange={(e) => setAnswer(currentStep.key, e.target.value)}
            placeholder="0000-000"
            maxLength={8}
          />
        );
      default:
        return null;
    }
  };

  // -------------------------------
  // Header title/desc for shell
  // -------------------------------
  const shellTitle = "Diagnóstico Legal Inteligente";
  let shellDesc = template?.intro_message || "Responda a algumas perguntas para gerarmos um diagnóstico técnico personalizado.";
  let shellStep = stepIdx;
  let shellTotal = Math.max(1, totalSteps + 2); // + análise + identificação

  if (phase === "form" && currentStep) {
    shellDesc = currentStep.title;
    shellStep = stepIdx;
  } else if (phase === "analyzing") {
    shellDesc = "A gerar o seu diagnóstico técnico personalizado…";
    shellStep = totalSteps;
  } else if (phase === "result") {
    shellDesc = "Diagnóstico gerado. Reveja e receba a versão completa por email.";
    shellStep = totalSteps;
  } else if (phase === "identity") {
    shellDesc = "Identifique-se para receber o relatório completo.";
    shellStep = totalSteps + 1;
  }

  const sections = phase === "result" || phase === "analyzing" || phase === "unlocked" ? parseSections(aiText) : [];

  // Footer sticky por fase
  const footer = (() => {
    if (loadingTpl || error) return null;
    if (phase === "form" && currentStep) {
      return (
        <>
          {stepIdx > 0 && <BackBtn testId="diag-back" onClick={goBack} />}
          <PrimaryBtn testId="diag-next" onClick={goNext} disabled={!canAdvance()}>
            {stepIdx === totalSteps - 1 ? (
              <span className="inline-flex items-center gap-2"><Sparkles size={14} /> Gerar Diagnóstico</span>
            ) : ("Continuar")}
          </PrimaryBtn>
        </>
      );
    }
    if (phase === "result") {
      return (
        <>
          <BackBtn testId="diag-back-result" onClick={goBack} />
          <PrimaryBtn testId="diag-to-identity" onClick={() => setPhase("identity")}>
            Receber Relatório Grátis
          </PrimaryBtn>
        </>
      );
    }
    if (phase === "identity") {
      return (
        <>
          <BackBtn testId="diag-back-identity" onClick={goBack} />
          <PrimaryBtn type="submit" testId="diag-submit" disabled={sending} onClick={() => {
            const form = document.getElementById("diag-identity-form");
            if (form) form.requestSubmit();
          }}>
            {sending ? (
              <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> A enviar…</span>
            ) : ("Desbloquear Relatório")}
          </PrimaryBtn>
        </>
      );
    }
    if (phase === "unlocked") {
      return (
        <PrimaryBtn
          testId="diag-close-unlocked"
          onClick={() => {
            onClose();
            // reset após fechar
            setStepIdx(0);
            setAnswers({});
            setId({});
            setAiText("");
            setPhase("form");
          }}
        >
          Concluir
        </PrimaryBtn>
      );
    }
    return null;
  })();

  return (
    <>
    <WizardShell
      open={open}
      onClose={onClose}
      testId="dialog-diagnostico"
      step={suspended ? 1 : shellStep}
      total={suspended ? 1 : shellTotal}
      title={shellTitle}
      desc={suspended ? "" : shellDesc}
      footer={suspended ? null : footer}
    >
      {suspended ? (
        <SuspendedNotice
          title={suspended.title}
          message={suspended.message}
          onClose={onClose}
          testId="diag-suspended"
        />
      ) : (
      <>
      {loadingTpl && (
        <div className="py-8 flex items-center justify-center text-slate-500 dark:text-slate-300 text-sm">
          <Loader2 size={16} className="animate-spin mr-2" /> A carregar questionário…
        </div>
      )}

      {error && !loadingTpl && (
        <div className="py-6 text-center">
          <AlertTriangle className="mx-auto text-red-500 mb-2" size={22} />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {!loadingTpl && !error && phase === "form" && currentStep && (
        <div className="space-y-4" data-testid={`diag-step-${currentStep.key}`}>
          {currentStep.help && (
            <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2">
              {currentStep.help}
            </p>
          )}
          {renderStepBody()}
        </div>
      )}

      {!loadingTpl && !error && (phase === "analyzing" || phase === "result" || phase === "unlocked") && (
        <div className="space-y-4">
          {/* Banner de sucesso quando desbloqueado */}
          {phase === "unlocked" && (
            <div
              className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30 px-4 py-3"
              data-testid="diag-unlocked-banner"
            >
              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div className="text-sm leading-relaxed text-emerald-900 dark:text-emerald-100">
                <p className="font-semibold">Diagnóstico desbloqueado</p>
                <p className="text-[12px] mt-0.5 text-emerald-800/90 dark:text-emerald-200/90">
                  O relatório completo está disponível abaixo. Enviámos também uma cópia para <span className="font-mono">{id.email}</span>.
                </p>
              </div>
            </div>
          )}
          {phase === "analyzing" && !aiText && (
            <div className="py-8 text-center">
              <Loader2 size={22} className="animate-spin mx-auto text-[#1698AC]" />
              <p className="text-xs text-slate-500 dark:text-slate-300 mt-2">A analisar respostas…</p>
            </div>
          )}
          {aiError && (
            <div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 text-sm text-red-700 dark:text-red-300">
              {aiError}
            </div>
          )}
          {sections.length > 0 && (() => {
            // Gate: só bloqueia quando phase="result". Em "analyzing" mostra streaming livre;
            // em "unlocked" mostra tudo em claro (após submissão do email).
            const isLocked = phase === "result" && !aiError;
            const VISIBLE_COUNT = 2;
            const renderableSections = sections.filter((s) => s.title || s.body.trim());
            const visibleSections = isLocked ? renderableSections.slice(0, VISIBLE_COUNT) : renderableSections;
            const lockedSections = isLocked ? renderableSections.slice(VISIBLE_COUNT) : [];
            const renderSection = (s, i, opts = {}) => {
              const Icon = SECTION_ICON[s.title] || Sparkles;
              return (
                <div
                  key={`${opts.prefix || "sec"}-${i}`}
                  className="border border-slate-200 dark:border-white/10 rounded-lg p-4 bg-white/60 dark:bg-white/5"
                  data-testid={`diag-section-${opts.locked ? "locked-" : ""}${i}`}
                  aria-hidden={opts.locked || undefined}
                >
                  {s.title && (
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={14} className="text-[#1698AC]" />
                      <p className="text-xs font-semibold tracking-wider uppercase text-slate-700 dark:text-slate-200">
                        {s.title}
                      </p>
                    </div>
                  )}
                  <SectionBody title={s.title} body={s.body} />
                </div>
              );
            };
            return (
              <div data-lenis-prevent className="space-y-3 max-h-[420px] overflow-y-auto overscroll-contain scrollbar-visible pr-1" data-testid="diag-ai-result">
                {visibleSections.map((s, i) => renderSection(s, i))}

                {phase === "analyzing" && (
                  <div className="flex items-center gap-2 text-xs text-slate-400 py-1">
                    <Loader2 size={11} className="animate-spin" /> a gerar…
                  </div>
                )}

                {/* Preview bloqueado com CTA — só aparece quando o diagnóstico foi gerado (phase="result") */}
                {isLocked && lockedSections.length > 0 && (
                  <div className="relative mt-2" data-testid="diag-locked-preview">
                    {/* Secções bloqueadas — desfocadas por baixo do overlay */}
                    <div
                      className="space-y-3 select-none pointer-events-none"
                      style={{ filter: "blur(5px)", opacity: 0.55 }}
                      aria-hidden="true"
                    >
                      {lockedSections.map((s, i) => renderSection(s, i, { locked: true, prefix: "locked" }))}
                    </div>
                    {/* Fade progressivo por cima do conteúdo bloqueado */}
                    <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-white via-white/85 to-transparent dark:from-slate-900 dark:via-slate-900/85 pointer-events-none rounded-t-lg" />
                    {/* Card CTA sobreposta */}
                    <div className="absolute inset-x-0 top-6 flex items-start justify-center px-2 pointer-events-none">
                      <div
                        className="pointer-events-auto w-full max-w-[440px] rounded-2xl border border-[#1698AC]/25 bg-white dark:bg-slate-800 shadow-[0_10px_40px_-8px_rgba(22,152,172,0.35)] p-5 text-center"
                        data-testid="diag-unlock-card"
                      >
                        <div className="mx-auto w-11 h-11 rounded-full bg-[#1698AC]/10 flex items-center justify-center mb-2.5 ring-4 ring-[#1698AC]/5">
                          <Lock size={18} className="text-[#1698AC]" />
                        </div>
                        <p className="text-[10px] font-bold tracking-[0.24em] uppercase text-[#1698AC] mb-1.5">
                          Diagnóstico completo
                        </p>
                        <h4 className="font-heading text-lg leading-tight text-slate-900 dark:text-slate-100">
                          Mais {lockedSections.length} {lockedSections.length === 1 ? "secção" : "secções"} disponíveis
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                          Obrigações legais aplicáveis, periodicidade, ações recomendadas e estimativa indicativa para o seu caso.
                        </p>
                        <button
                          type="button"
                          onClick={() => setPhase("identity")}
                          data-testid="diag-unlock-cta"
                          className="mt-4 w-full h-11 rounded-full bg-[#1698AC] hover:bg-[#0F7484] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors shadow-md hover:shadow-lg active:scale-[0.98]"
                        >
                          <Mail size={15} /> Receber Relatório Grátis
                        </button>
                        <p className="text-[10px] text-slate-400 dark:text-slate-300 mt-2.5">
                          Sem custos · Enviado em minutos · Confidencial (RGPD)
                        </p>
                      </div>
                    </div>
                    {/* Espaço reservado para não sobrepor à barra inferior */}
                    <div className="h-8" />
                  </div>
                )}
              </div>
            );
          })()}
          {/* Footer buttons handled by WizardShell footer prop */}
        </div>
      )}

      {!loadingTpl && !error && phase === "identity" && (
        <form id="diag-identity-form" onSubmit={finish} className="space-y-4">
          <IdentityFields
            prefix="diag"
            values={id}
            onChange={setId}
            errors={identityErrors}
            touched={idTouched}
            onBlur={setIdBlur}
          />
          <Honeypot inputRef={hpRef} valueRef={hpValRef} />
        </form>
      )}

      {/* phase "done" já não é utilizado — a exibição desbloqueada é feita
          por "unlocked" no bloco de resultado acima. Mantido vazio para retrocompatibilidade. */}
      </>
      )}
    </WizardShell>
    <SuccessModal
      open={!!successData}
      onClose={() => { setSuccessData(null); }}
      title="Diagnóstico desbloqueado!"
      eyebrow="DIAGNÓSTICO LEGAL"
      name={successData?.name}
      email={successData?.email}
      details={successData?.details || []}
      message="O relatório completo com as obrigações legais aplicáveis está agora visível. Enviámos também uma cópia para o seu email."
      testId="diag-success-modal"
    />
    </>
  );
}
